import { CookieConsent } from "@components/CookieConsent";
import { usePreferences } from "@contexts/PreferenceContext";
import { useMediaQuery } from "@repo/ui/hooks";
import { ConfirmHost } from "@repo/ui/na";
import { useEffect, useRef, useState } from "react";
import { Outlet, useLocation, useSearchParams } from "react-router";
import { SWRConfig } from "swr";
import { trackLoading, useIsLoading } from "@app/loading";
import { usePageviews } from "../analytics";
import { DeployModal } from "@screens/emulators/DeployModal";
import { CommandPalette } from "./CommandPalette";
import { FeedbackModal } from "./FeedbackModal";
import { Header } from "./Header";
import classes from "./shell.module.scss";
import { Sidebar } from "./Sidebar";
import { useNavModel } from "./useNavModel";

const EMU_VIEW = /^\/emulators\/[^/]+/;

/**
 * Authenticated app shell: sidebar (216 / 57), 52px header, scrolling main.
 * The emulator view auto-collapses the sidebar and manages its own scroll.
 */
export default function AppShell() {
  return (
    <SWRConfig value={{ use: [trackLoading] }}>
      <Shell />
    </SWRConfig>
  );
}

function Shell() {
  usePageviews();
  const { pathname } = useLocation();
  const { preferences, setPreference } = usePreferences();
  const nav = useNavModel();
  const inEmuView = EMU_VIEW.test(pathname);

  // `?tenant=<id>` (legacy tenant-admin links) switches the active tenant once.
  const [params, setParams] = useSearchParams();
  const tenantParam = params.get("tenant");
  const { setTenant, tenants } = nav.session;
  useEffect(() => {
    if (!tenantParam || !tenants.some((t) => t.id === tenantParam)) return;
    setTenant(tenantParam);
    params.delete("tenant");
    setParams(params, { replace: true });
  }, [tenantParam, tenants, setTenant, params, setParams]);

  // Entering the emulator view or a narrow window (tablets, split screens)
  // collapses to the icon rail; toggling there is temporary, and leaving
  // restores the saved preference.
  const narrow = useMediaQuery("(max-width: 1100px)") ?? false;
  const auto = inEmuView || narrow;
  const [autoCollapsed, setAutoCollapsed] = useState(true);
  useEffect(() => setAutoCollapsed(true), [inEmuView, narrow]);
  const collapsed = auto ? autoCollapsed : Boolean(preferences?.sidebarCollapsed);
  const toggle = () =>
    auto ? setAutoCollapsed((c) => !c) : setPreference?.("sidebarCollapsed", !collapsed);

  // Tab titles follow the breadcrumbs: "Members · Organization · Null Autos".
  const title = [...nav.crumbs].reverse().map((c) => c.label).filter(Boolean).join(" · ");
  useEffect(() => {
    document.title = title ? `${title} · Null Autos` : "Null Autos";
  }, [title]);

  useEffect(() => {
    document.documentElement.dataset.naReduceMotion = String(Boolean(preferences?.reduceMotion));
  }, [preferences?.reduceMotion]);

  // Accent line pulses while anything loads, and only stops at the end of a
  // cycle so it settles back to the resting gradient. Sub-150ms loads skip it.
  const loading = useIsLoading();
  const loadingRef = useRef(loading);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    loadingRef.current = loading;
    if (!loading) return;
    const t = setTimeout(() => setPulse(true), 150);
    return () => clearTimeout(t);
  }, [loading]);

  return (
    <div className={classes.app}>
      <div
        className={classes.accent}
        data-pulse={pulse || undefined}
        onAnimationIteration={() => !loadingRef.current && setPulse(false)}
        aria-hidden
      />
      <Sidebar sections={nav.sections} bottom={nav.bottom} collapsed={collapsed} onToggle={toggle} />
      <div className={classes.column}>
        <Header
          crumbs={nav.crumbs}
          running={nav.emus.running.length}
          session={nav.session}
          gitlabLinked={nav.bottom[0]?.badge ? false : undefined}
        />
        <main className={classes.main} data-fixed={inEmuView || undefined}>
          <Outlet />
        </main>
      </div>
      <CommandPalette sections={[...nav.sections, ...nav.bottom]} emulators={nav.emus.rows} session={nav.session} />
      <DeployModal />
      <FeedbackModal />
      <ConfirmHost />
      <CookieConsent />
    </div>
  );
}
